import { useState } from "react";
import { Link, useOutletContext, useParams } from "react-router";
import { costLabel, costsLabel, findAccount, money } from "./internalAccounts.js";
import InternalTable from "./InternalTable.jsx";

const VISITS = ["preventiveCare", "pcpVisit", "specialistVisit", "obgynVisit", "behavioralHealthOffice", "urgentCare", "virtualUrgentCare", "virtualBehavioralHealth"];
const HOSPITAL = ["emergencyRoom", "inpatientHospital", "outpatientSurgery", "groundAmbulance", "airAmbulance"];
const EVERYDAY = ["outpatientRehabilitation", "chiropractice", "acupuncture", "homeHealthCare", "hospiceProfessionalCare", "basicLabImaging", "basiclabs", "basicDiagnostic/radiology", "advanceDiagnostic", "wigs"];

const LABELS = {
    preventiveCare: "Preventive",
    pcpVisit: "PCP visit",
    specialistVisit: "Specialist",
    obgynVisit: "OB/GYN",
    behavioralHealthOffice: "Behavioral health",
    urgentCare: "Urgent care",
    virtualUrgentCare: "Virtual urgent care",
    virtualBehavioralHealth: "Virtual behavioral health",
    emergencyRoom: "Emergency room",
    inpatientHospital: "Inpatient hospital",
    outpatientSurgery: "Outpatient surgery",
    groundAmbulance: "Ground ambulance",
    airAmbulance: "Air ambulance",
    outpatientRehabilitation: "Rehabilitation",
    chiropractice: "Chiropractic",
    acupuncture: "Acupuncture",
    homeHealthCare: "Home health",
    hospiceProfessionalCare: "Hospice",
    basicLabImaging: "Labs and imaging",
    basiclabs: "Basic labs",
    "basicDiagnostic/radiology": "Basic radiology",
    advanceDiagnostic: "Advanced imaging",
    wigs: "Wigs",
    gastricBypassBariatric: "Bariatric",
    genderAffirmingMedicalAndSurgicalTreatment: "Gender affirming care",
    abortionElective: "Elective abortion",
    transplantTravel: "Transplant travel",
    nonTransplantTravel: "Other travel",
    impactedTeeth: "Impacted teeth",
    infertilityTreatment: "Infertility",
    cellAndGeneTherapy: "Cell and gene therapy",
    hearingAids: "Hearing aids",
    generic: "Generic",
    preferred: "Preferred",
    nonPrefered: "Non-preferred",
    speciality: "Specialty",
};

const label = (key) => LABELS[key] || key;

const limitLabel = (limit) => {
    const unit = limit.type === "dollarsPerLifetime"
        ? "per lifetime"
        : limit.type === "dollarsPerPlanYear"
            ? "per year"
            : "days per year";
    const value = String(limit.type || "").startsWith("dollars") ? money(limit.value) : limit.value;
    const shared = limit.sharedWith?.length ? ` shared with ${limit.sharedWith.join(", ")}` : "";
    return `${value} ${unit}${shared}`;
};

const codeBits = (service) => [
    ...(service.cptCodes || []),
    ...(service.cptCodeGroups || []).map((group) => `${group.name} ${group.from}–${group.to}`),
    ...(service.hcpcsCodes || []),
    ...(service.hcpcsCodeGroups || []).map((group) => `${group.name} ${group.from}–${group.to}`),
    ...(service.revenueCode || []),
    ...(service.revenueCodeGroups || []).map((group) => `${group.name} ${group.from}–${group.to}`),
];

const serviceDetail = (service) => [
    service.isCovered === false ? "Excluded" : costsLabel(service),
    service.deductibleApplies === true ? "Deductible applies" : service.deductibleApplies === false ? "Deductible waived" : "",
    ...(service.limits || []).map(limitLabel),
    ...(service.conditions || []),
    service.maxNumberOfDaysCovered != null ? `${service.maxNumberOfDaysCovered} days` : "",
    service.nonEmergentFacility ? `Non-emergent facility ${costsLabel(service.nonEmergentFacility)}` : "",
    service.professional ? `Emergent professional ${costsLabel(service.professional)}` : "",
    service.professionalNonEmergent ? `Non-emergent professional ${costsLabel(service.professionalNonEmergent)}` : "",
    service.penalty?.value != null ? `${money(service.penalty.value)} penalty${service.penalty.condition ? `, ${service.penalty.condition}` : ""}` : "",
    service.nonEmergentPenalty ? `${money(service.nonEmergentPenalty.value)} if not emergent` : "",
    service.noReferralPenalty ? `${money(service.noReferralPenalty.value)} if no referral` : "",
    ...[
        ["withPcpVisit", "With PCP visit"],
        ["withObgynVisit", "With OB/GYN visit"],
        ["withSpecialistVisit", "With specialist visit"],
    ].map(([field, name]) => {
        const share = service[field];
        if (!share) return "";
        return `${name} ${[
            share.isCovered === false ? "Excluded" : costsLabel(share),
            share.renderedByMemberPcp ? "rendered by member's PCP" : "",
        ].filter(Boolean).join(", ")}`.trim();
    }),
    service.precertification ? `Precertification ${[
        service.precertification.required === false ? "not required" : service.precertification.required ? "required" : "",
        service.precertification.penalty?.valueType === "percent" ? `${service.precertification.penalty.value}%` : money(service.precertification.penalty?.value),
        service.precertification.responsibility === "provider" ? "provider pays" : service.precertification.responsibility === "member" ? "member pays" : "",
    ].filter(Boolean).join(", ")}`.trim() : "",
].filter(Boolean).join(" · ");

const Chunk = ({ title, network, keys, hot }) => {
    const lines = keys.filter((key) => network?.[key]);
    if (!lines.length) return null;
    return (
        <section className="internal-work__chunk">
            <header><h2>{title}</h2></header>
            <InternalTable
                columns={hot ? ["Service", "Detail", "Codes"] : ["Service", "Detail"]}
                rows={lines.map((key) => ({
                    key,
                    cells: hot
                        ? [label(key), serviceDetail(network[key]), codeBits(network[key]).join(", ")]
                        : [label(key), serviceDetail(network[key])],
                }))}
            />
        </section>
    );
};

const Rx = ({ network }) => {
    const tiers = network?.rx?.tier;
    if (!tiers) return null;
    return (
        <section className="internal-work__chunk">
            <header><h2>Prescriptions</h2></header>
            <InternalTable
                columns={["Tier", "Supply"]}
                rows={Object.entries(tiers).map(([tier, supply]) => ({
                    key: tier,
                    cells: [
                        label(tier),
                        Object.entries(supply || {})
                            .filter(([, cost]) => cost && typeof cost === "object")
                            .map(([days, cost]) => `${days} ${costLabel(cost)}`.trim())
                            .join(", "),
                    ],
                }))}
            />
        </section>
    );
};

const peakFor = (plan, network, deskId) => {
    if (deskId === "plan-config") {
        const coded = Object.values(network || {}).filter((service) => service && codeBits(service).length).length;
        const strong = coded === 1 ? "1 service has codes" : coded ? `${coded} services have codes` : "No codes yet";
        return { strong, text: "CPT, HCPCS, and revenue codes sit on the service." };
    }
    if (deskId === "underwriter" || deskId === "proposal") {
        const deductible = network?.deductible;
        return {
            strong: deductible ? `${money(deductible.individual)} individual` : plan.planAdministration,
            text: deductible ? `Family ${money(deductible.family)} · ${plan.accumulators?.deductible?.type || "Deductible"}` : plan.planAdministration,
        };
    }
    if (deskId === "client-success") {
        return { strong: plan.utilizationManagement?.vendorName || vendorNames(plan.telemedicine) || "No vendor", text: "Utilization management and telemedicine." };
    }
    const pcp = costsLabel(network?.pcpVisit);
    return { strong: pcp ? `PCP ${pcp}` : plan.planName, text: plan.planAdministration || "The benefit plan they bought." };
};

const vendorNames = (value) => {
    if (Array.isArray(value)) return value.map((item) => item.vendor).filter(Boolean).join(", ");
    return value?.vendor || "";
};

const PlanView = () => {
    const { desk } = useOutletContext();
    const { accountId, planName } = useParams();
    const [side, setSide] = useState("inNetwork");
    const account = findAccount(accountId);
    const plan = account?.benefitPlans?.find((row) => row.planName === decodeURIComponent(planName));

    if (!account || !plan) {
        return (
            <>
                <Link className="internal-work__back" to={`/internal/${desk.id}`}>Accounts</Link>
                <h1>That plan is not on the account</h1>
            </>
        );
    }

    const network = plan[side] || {};
    const peak = peakFor(plan, plan.inNetwork, desk.id);
    const hot = desk.id === "plan-config";
    const excluded = Object.entries(network.excludedServices || {});

    return (
        <>
            <Link className="internal-work__back" to={`/internal/${desk.id}/${account.id}`}>{account.accountName}</Link>
            <p className="internal-work__eyebrow">{desk.label}</p>
            <h1>{plan.planName}</h1>
            <div className="internal-work__peak">
                <strong>{peak.strong}</strong>
                <span>{peak.text}</span>
            </div>
            <div className="internal-work__switch" role="tablist">
                <button type="button" className={side === "inNetwork" ? "is-on" : ""} onClick={() => setSide("inNetwork")}>In network</button>
                <button type="button" className={side === "outOfNetwork" ? "is-on" : ""} onClick={() => setSide("outOfNetwork")}>Out of network</button>
            </div>
            {network.coinsurance && (
                <p className="internal-work__lead">This network is {costLabel(network.coinsurance)} after the deductible.</p>
            )}
            <div className="internal-work__chunks">
                <section className="internal-work__chunk">
                    <header><h2>Census tiers</h2></header>
                    <InternalTable
                        columns={["Census tier", "Code", "Dependent age"]}
                        rows={(plan.tiers || []).map((tier) => ({
                            key: tier.code || tier.tierName,
                            cells: [
                                tier.tierName,
                                tier.code,
                                tier.dependentAge ? `${tier.dependentAge.value} ${tier.dependentAge.condition || ""}`.trim() : "",
                            ],
                        }))}
                    />
                    {(plan.tiers || []).length === 0 && <p>No census tiers on this plan.</p>}
                </section>
                {(desk.id === "underwriter" || desk.id === "proposal") && (
                    <section className="internal-work__chunk is-focus">
                        <header><h2>Deductible and out-of-pocket</h2></header>
                        <InternalTable
                            columns={["Item", "Amount"]}
                            rows={[
                                { key: "deductible", cells: ["Deductible", `${money(network.deductible?.individual)} / ${money(network.deductible?.family)}`] },
                                { key: "oop", cells: ["Out of pocket", `${money(network.outOfPocketMaximum?.individual)} / ${money(network.outOfPocketMaximum?.family)}`] },
                                network.coinsurance && { key: "coinsurance", cells: ["Coinsurance", costLabel(network.coinsurance)] },
                            ].filter(Boolean)}
                        />
                    </section>
                )}
                {desk.id === "client-success" && (
                    <section className="internal-work__chunk is-focus">
                        <header><h2>Vendors</h2></header>
                        <p>{vendorNames(plan.telemedicine) ? `Telemedicine · ${vendorNames(plan.telemedicine)}` : "No telemedicine vendor."}</p>
                        <p>{vendorNames(plan.pbm) ? `PBM · ${vendorNames(plan.pbm)}` : "No PBM."}</p>
                        <p>{plan.utilizationManagement?.vendorName ? `Utilization management · ${plan.utilizationManagement.vendorName}` : "No utilization vendor."}</p>
                    </section>
                )}
                <Chunk title="Visits" network={network} keys={VISITS} hot={hot} />
                <Chunk title="Hospital" network={network} keys={HOSPITAL} hot={hot} />
                <Chunk title="Everyday care" network={network} keys={EVERYDAY} hot={hot} />
                <Rx network={network} />
                {excluded.length > 0 && (
                    <section className="internal-work__chunk">
                        <header><h2>Exclusions</h2></header>
                        <InternalTable
                            columns={hot ? ["Service", "Detail", "Codes"] : ["Service", "Detail"]}
                            rows={excluded.map(([key, service]) => ({
                                key,
                                cells: hot
                                    ? [label(key), serviceDetail(service), codeBits(service).join(", ")]
                                    : [label(key), serviceDetail(service)],
                            }))}
                        />
                    </section>
                )}
            </div>
            <p className="internal-work__end">{desk.writes}</p>
        </>
    );
};

export default PlanView;
